// Pure helpers for the Notes review module (`/_/review/note`).
//
// No Vue imports and no side effects — everything here is unit-tested with
// Vitest (ts/lib/transcript.test.ts). The module owns the pieces of the
// review screen that are pure logic:
//
// - display formatting (`transcriptTitle`, `formatTranscriptDuration`) and the
//   shared NLU layers verdict badge mapping (queue row + detail panel);
// - playback mapping: where the playhead is (line/word under a time) and how
//   a word click maps to a seek target;
// - payload shaping for the Phase 4 mutation endpoints (edit/status/
//   extraction/review), so the components send exactly the api.ts contract.

import type {
	TranscriptClarifyRequest,
	TranscriptEditRequest,
	TranscriptExtraction,
	TranscriptExtractionKind,
	TranscriptExtractionRequest,
	TranscriptLayersStatus,
	TranscriptLine,
	TranscriptNote,
	TranscriptPropertyEntry,
	TranscriptReviewRequest,
	TranscriptReviewStatus,
	TranscriptStatusRequest,
	TranscriptWord,
} from "@/type/api";

/** Human-readable title, e.g. "Transcript #4821". */
export function transcriptTitle(
	transcript: Pick<TranscriptNote, "id">,
): string {
	return `Transcript #${transcript.id}`;
}

/** Format seconds as mm:ss, e.g. 83 → "01:23" (fractional inputs floor). */
export function formatTranscriptDuration(totalSeconds: number): string {
	const seconds = Math.floor(totalSeconds);
	const minutes = Math.floor(seconds / 60);
	return `${String(minutes).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

/**
 * Bootstrap badge class for an NLU layers verdict: "matched" is a clean pass
 * (success), "drifted" is the alarm the drift rows evidence (danger), and
 * "unverified" is a payload the server could not check (warning). Shared by
 * the queue row and the detail panel so the same verdict cannot read as two
 * different colors depending on where it is seen.
 */
export function layersStatusBadgeClass(status: TranscriptLayersStatus): string {
	switch (status) {
		case "matched":
			return "bg-success";
		case "drifted":
			return "bg-danger";
		case "unverified":
			return "bg-warning text-dark";
	}
}

/** Ontology kinds that have a review page at `/_/review/<kind>/:id`. */
const REVIEWABLE_DERIVED_KINDS: Record<string, true> = {
	site: true,
	feature: true,
	inspection: true,
	treatment: true,
};

/**
 * Review-workbench path for a derived resource, or null when the kind has no
 * review page (observation, assignment, facilitator, constraint kinds) — the
 * caller renders those as plain text rather than a broken link.
 */
export function derivedResourceReviewPath(
	kind: string,
	id: number,
): string | null {
	// `=== true` (not `in`) so prototype members ("toString", …) never match.
	if (REVIEWABLE_DERIVED_KINDS[kind] !== true) return null;
	return `/_/review/${kind}/${id}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Playback mapping — where the playhead is in the transcript.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Index of the transcript line under `time` (the last line that started at or
 * before `time`), or undefined when `time` precedes every line or there are
 * no lines. Lines without word timestamps (edited/legacy) still participate
 * so the line highlight keeps working while their text is spoken.
 */
export function lineIndexAtTime(
	lines: readonly TranscriptLine[],
	time: number,
): number | undefined {
	if (lines.length === 0 || time < 0) return undefined;
	let current: number | undefined;
	for (let i = 0; i < lines.length; i++) {
		if (lines[i].startSeconds <= time) current = i;
		else break;
	}
	return current;
}

/**
 * Index of the word under `time` within one line's words — the word whose
 * window contains `time` (start ≤ time < end). Undefined when `time` falls
 * outside every word (a gap, before the first word, or after the last) or
 * the line has no words. Word windows are contiguous per contract, so a time
 * exactly on a boundary resolves to the word starting there.
 */
export function wordIndexAtTime(
	words: readonly TranscriptWord[],
	time: number,
): number | undefined {
	if (time < 0) return undefined;
	for (let i = 0; i < words.length; i++) {
		const word = words[i];
		if (time >= word.start && time < word.end) return i;
	}
	return undefined;
}

/**
 * The (line, word) position under `time`, used for the karaoke highlight.
 * Undefined when the time is not inside a word (no lines, before the first
 * word, a gap, empty words, or past the transcript).
 */
export interface TranscriptPlaybackPosition {
	lineIndex: number;
	wordIndex: number;
}

export function playbackPosition(
	lines: readonly TranscriptLine[],
	time: number,
): TranscriptPlaybackPosition | undefined {
	const lineIndex = lineIndexAtTime(lines, time);
	if (lineIndex === undefined) return undefined;
	const wordIndex = wordIndexAtTime(lines[lineIndex].words, time);
	if (wordIndex === undefined) return undefined;
	return { lineIndex, wordIndex };
}

/** Clamp a seek target into [0, duration]; 0 when duration is unknown. */
export function clampSeekTime(time: number, duration: number): number {
	if (!Number.isFinite(duration) || duration <= 0) return 0;
	return Math.min(Math.max(time, 0), duration);
}

/** Seek target for clicking a word: its start, clamped to the recording. */
export function wordSeekTime(word: TranscriptWord, duration: number): number {
	return clampSeekTime(word.start, duration);
}

// ─────────────────────────────────────────────────────────────────────────────
// Payload shaping — exact bodies for the Phase 4 mutation endpoints.
// ─────────────────────────────────────────────────────────────────────────────

/** PUT /api/transcript/{id}/transcript — one corrected line. */
export function transcriptEditPayload(
	lineId: number,
	text: string,
): TranscriptEditRequest {
	return { lineId, text };
}

/** PUT /api/transcript/{id}/status — review lifecycle transition. */
export function transcriptStatusPayload(
	status: TranscriptReviewStatus,
): TranscriptStatusRequest {
	return { status };
}

/** POST /api/transcript/{id}/clarify — question to the technician. */
export function transcriptClarifyPayload(
	question: string,
): TranscriptClarifyRequest {
	return { question };
}

/**
 * Parse a decimal-string record id (as the API reports it, e.g. note "4821",
 * extraction "7") into the numeric id the mutation bodies carry. Throws on
 * non-numeric ids so a broken record fails loudly instead of silently
 * sending 0 to the server.
 */
export function numericRecordId(id: string): number {
	const numeric = Number(id);
	if (!Number.isInteger(numeric) || numeric <= 0) {
		throw new Error(`record id is not a positive integer: "${id}"`);
	}
	return numeric;
}

/**
 * POST /api/transcript/{id}/extraction — correction of a proposed record:
 * the record's kind/label and transcript window are preserved, `supersedesId`
 * addresses the record being corrected, and the corrected summary + field
 * values ride along (unit/derived/anomalous/attribution metadata is kept as
 * the server reported it — the server recomputes flags on the new row).
 */
export function extractionCorrectionPayload(
	original: TranscriptExtraction,
	summary: string,
	fieldValues: ReadonlyMap<string, string>,
): TranscriptExtractionRequest {
	const fields = original.fields.map((field) => {
		const corrected = fieldValues.get(field.key);
		const value = corrected === undefined ? field.value : corrected.trim();
		return { ...field, value };
	});
	const payload: TranscriptExtractionRequest = {
		kind: original.kind,
		label: original.label,
		summary,
		fields,
	};
	if (original.transcriptRef) {
		payload.startSeconds = original.transcriptRef.startSeconds;
		payload.endSeconds = original.transcriptRef.endSeconds;
	}
	payload.supersedesId = numericRecordId(original.id);
	return payload;
}

/**
 * POST /api/review — attach a comment/finding to one reviewed object (the
 * whole note, or a single extraction card). `objectId` is the record's
 * decimal-string id as the API reports it; the wire body carries the number.
 */
export function reviewObjectPayload(
	objectType: "transcript" | TranscriptExtractionKind,
	objectId: string,
	findings: string,
): TranscriptReviewRequest {
	return {
		scopeType: "object",
		reviewType: "data_quality",
		findings,
		objects: [{ objectType, objectId: numericRecordId(objectId) }],
	};
}

/**
 * POST /api/review — link a correction to both extraction rows (old +
 * superseding new), the Review artifact recording that the reviewer replaced
 * one record with another.
 */
export function correctionReviewPayload(
	kind: TranscriptExtractionKind,
	supersededId: string,
	replacementId: string,
	findings: string,
): TranscriptReviewRequest {
	return {
		scopeType: "object",
		reviewType: "data_quality",
		findings,
		objects: [
			{ objectType: kind, objectId: numericRecordId(supersededId) },
			{ objectType: kind, objectId: numericRecordId(replacementId) },
		],
	};
}

/** A transcript line as the review panel renders it. */
export interface TranscriptLineView {
	/** Line row id; null → synthesized/legacy → not editable. */
	id: number | null;
	startSeconds: number;
	/** Current text — the reviewer's edit when the line has been corrected. */
	text: string;
	/** Original (struck-through) text when the server has an edit; absent otherwise. */
	original?: string;
	words: TranscriptWord[];
}

/**
 * Adapt a server transcript line for rendering: edited lines (server carries
 * `original`) render their corrected text with the original struck through —
 * their words no longer match the corrected text, so karaoke is suppressed.
 */
export function toTranscriptLineView(line: TranscriptLine): TranscriptLineView {
	return {
		id: line.id,
		startSeconds: line.startSeconds,
		text: line.text,
		original: line.original ?? undefined,
		// Server word timing only matches the current text on unedited lines.
		words: line.original ? [] : line.words,
	};
}

/**
 * The editable field values of an extraction card, keyed by field key, for
 * the correction form (derived fields are read-only — recomputed
 * server-side — so they are excluded from the editable set).
 */
export function editableFieldValues(
	fields: readonly TranscriptPropertyEntry[],
): Map<string, string> {
	const values = new Map<string, string>();
	for (const field of fields) {
		if (!field.derived) values.set(field.key, field.value);
	}
	return values;
}
